/**
 * Line arithmetic for the estimate builder (M2-06) — the API's `Money`
 * rules, restated so a line's total appears as the plumber types rather than a
 * round trip later.
 *
 * <b>⚠️ Exact integer arithmetic, never floating point.</b> `0.1 * 3` is
 * `0.30000000000000004` in JavaScript, and the API's `ck_line_items_total`
 * compares cents exactly. Every amount is converted to an integer count of
 * its smallest unit (cents; thousandths for a quantity; hundredths of a
 * percent) and multiplied as a `BigInt`, so no product can lose a digit —
 * 99,999.99 × 99,999.99 in thousandths × cents is past `Number`'s 2^53.
 *
 * <b>⚠️ Half away from zero, like Postgres</b> — not `Math.round` (which rounds
 * −0.5 up) and not `toFixed` (which rounds the binary value, so `1.005`
 * becomes `1.00`). Half an hour at $0.25 is exactly $0.125 and has to come out
 * $0.13, or the line the builder shows is not the line the server stores.
 *
 * The server stays the authority: the preview endpoint prices the same lines
 * and its tax and total are what the builder shows for those. This is only
 * what makes the per-line figures instant.
 */

/** A discount as the API spells it. */
export type DiscountType = 'Percent' | 'Amount';

/** The API's `Money.Max`: the largest price, quantity or amount anyone may type. */
export const MAX_AMOUNT = 99_999.99;

/**
 * A typed decimal as an integer count of `10^-scale` units, or null when it
 * is not a plain non-negative decimal with at most `scale` places. `"1.5"`
 * at scale 3 is `1500n`. Rejects rather than rounds: an over-precise entry is
 * a validation message, not a silent change to what somebody typed.
 */
export function toUnits(text: string, scale: number): bigint | null {
  const match = /^(\d+)(?:\.(\d*))?$/.exec(text.trim());
  if (!match) {
    return null;
  }
  const [, whole, fraction = ''] = match;
  if (fraction.length > scale) {
    return null;
  }
  return BigInt(whole! + fraction.padEnd(scale, '0'));
}

/** `numerator / denominator`, rounded half away from zero. Both non-negative. */
function divideRounded(numerator: bigint, denominator: bigint): bigint {
  return (numerator * 2n + denominator) / (denominator * 2n);
}

/** A line's gross in cents: quantity (thousandths) × unit price (cents), rounded once. */
export function grossCents(quantityThousandths: bigint, unitPriceCents: bigint): bigint {
  return divideRounded(quantityThousandths * unitPriceCents, 1000n);
}

/**
 * What a discount takes off, in cents. A percentage (in hundredths of a
 * percent) comes off the ROUNDED gross and is rounded once — the API's
 * `Money.Discount`. An amount is taken as written; that it may not exceed the
 * gross is a validation rule, not a clamp.
 */
export function discountCents(
  gross: bigint,
  type: DiscountType | null,
  value: bigint | null,
): bigint {
  if (type === null || value === null) {
    return 0n;
  }
  return type === 'Percent' ? divideRounded(gross * value, 10_000n) : value;
}

/** Cents as a plain number of dollars, for `formatCurrency`. Exact for any amount this app can hold. */
export function centsToNumber(cents: bigint): number {
  return Number(cents) / 100;
}
